import React from 'react';
import { ChevronDown, ChevronRight, GitBranch, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { TableCell, TableRow } from '@/components/ui/table';
import { fmtN } from './JoBits';

export const slug = (s) => String(s).replace(/[^A-Za-z0-9]+/g, '-');

const TILES = [['NQ sort nos', 'nq', 'text-amber-700'], ['Without NQ sort nos', 'non_nq', 'text-emerald-700']];

/** Two tiles: free stock pieces of the FG's NQ sort nos vs the rest (+ their parent sort nos, counted once). */
export const NqTiles = ({ data }) => (
  <div className="grid grid-cols-2 gap-2" data-testid="gs-jo-nq-tiles">
    {TILES.map(([label, k, tone]) => (
      <div key={k} className="rounded-lg border border-[#E5DFD0] bg-white px-3 py-2" data-testid={`gs-jo-tile-${slug(k)}`}>
        <div className="text-[11px] uppercase tracking-wide text-gray-500">{label} · free pcs</div>
        <div className={`text-xl font-bold tabular-nums ${tone}`} data-testid={`gs-jo-tile-${slug(k)}-pcs`}>{fmtN(data[`${k}_free_pcs`])}</div>
        <div className="text-[11px] text-gray-500">{data[`${k}_sorts`]} sort no{data[`${k}_sorts`] === 1 ? '' : 's'} in stock
          {data[`${k}_parent_pcs`] > 0 && <span className="text-indigo-700" data-testid={`gs-jo-tile-${slug(k)}-parent`}> · +{fmtN(data[`${k}_parent_pcs`])} pcs in parent sort nos</span>}</div>
      </div>
    ))}
  </div>
);

const ColourRow = ({ id, c, value, onChange, disabled, tone = '' }) => (
  <TableRow className={`hover:bg-[#F5EFE3] ${tone || 'bg-[#FBF8F2]'}`} data-testid={`gs-jo-colour-${id}`}>
    <TableCell />
    <TableCell className="pl-6 text-sm text-gray-700">{c.color_name}</TableCell>
    <TableCell className="tabular-nums text-right text-sm text-gray-600">{c.free_bundles}</TableCell>
    <TableCell className="tabular-nums text-right text-sm text-gray-600" data-testid={`gs-jo-colour-free-${id}`}>{fmtN(c.left)}</TableCell>
    <TableCell className="w-[170px]">
      <Input inputMode="numeric" value={value || ''} placeholder="0" disabled={disabled || c.left <= 0 && !value} onChange={(e) => onChange(e.target.value)}
        className="h-8 text-right bg-white" data-testid={`gs-jo-colour-pcs-${id}`} />
    </TableCell>
  </TableRow>
);

const ParentHead = ({ a, s }) => (
  <TableRow className="bg-indigo-50/60 hover:bg-indigo-50/60" data-testid={`gs-jo-parent-head-${s}`}>
    <TableCell />
    <TableCell colSpan={4} className="py-1.5 text-xs text-indigo-800">
      <GitBranch className="w-3.5 h-3.5 inline mr-1" />From parent sort <span className="font-mono font-semibold">{a.parent.sort_no}</span> — changes to {a.sort_no} when picked
    </TableCell>
  </TableRow>
);

/** "2/3 colours" next to the row total: colours with pieces entered vs colours in stock (own + parent). */
const ColourHint = ({ s, avail, row }) => {
  const names = new Set([...avail.own, ...avail.parent].filter((c) => c.left > 0 || (row?.own?.[c.color_name] || row?.parent?.[c.color_name])).map((c) => c.color_name));
  const used = new Set([...Object.entries(row?.own || {}), ...Object.entries(row?.parent || {})].filter(([, n]) => n > 0).map(([c]) => c));
  const full = names.size > 0 && used.size >= names.size;
  return (
    <span className={`text-[11px] whitespace-nowrap rounded px-1.5 py-0.5 tabular-nums ${full ? 'bg-emerald-100 text-emerald-800' : used.size ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-600'}`}
      data-testid={`gs-jo-colour-hint-${s}`}>{used.size}/{names.size} colours</span>
  );
};

/** A base article row (row total + colour hint) with a colour dropdown: own colours, then the parent sort's colours. */
export const ArticleRow = ({ a, avail, open, onToggle, total, row, splitting, onTotal, onColour, disabled }) => {
  const s = slug(a.sort_no);
  return (
    <>
      <TableRow data-testid={`gs-jo-article-${s}`}>
        <TableCell className="w-8 px-1">
          <button type="button" onClick={onToggle} aria-label={open ? 'Hide colours' : 'Show colours'} data-testid={`gs-jo-toggle-${s}`}
            className="p-1 rounded hover:bg-[#F5EFE3] text-gray-600 transition-colors">
            {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
        </TableCell>
        <TableCell>
          <div className="font-mono text-sm">{a.sort_no}</div>
          <div className="text-[11px] text-gray-500 truncate max-w-[220px]">{[a.vendor_name, a.description].filter(Boolean).join(' · ')}</div>
          <div className="text-[11px] text-[#2D5F4F]" data-testid={`gs-jo-colours-count-${s}`}>{a.colors.length} colour{a.colors.length === 1 ? '' : 's'}{a.need_assortment ? ' · Need Assortment' : ''}</div>
          {a.parent && <div className="text-[11px] text-indigo-700" data-testid={`gs-jo-parent-${s}`}>Parent <span className="font-mono">{a.parent.sort_no}</span> · {fmtN(a.parent.free_pcs)} pcs free · {a.parent.colors.length} colour{a.parent.colors.length === 1 ? '' : 's'}</div>}
        </TableCell>
        <TableCell className="tabular-nums text-right">{a.free_bundles}</TableCell>
        <TableCell className={`tabular-nums text-right font-semibold ${a.free_pcs ? 'text-emerald-700' : 'text-gray-400'}`} data-testid={`gs-jo-free-${s}`}>{fmtN(a.free_pcs)}</TableCell>
        <TableCell className="w-[170px]">
          <div className="flex items-center gap-1.5">
            <ColourHint s={s} avail={avail} row={row} />
            <div className="relative flex-1 min-w-[72px]">
              <Input inputMode="numeric" value={total || ''} placeholder="0" disabled={disabled} onChange={(e) => onTotal(e.target.value)}
                className="h-9 text-right pl-7" data-testid={`gs-jo-pcs-${s}`} />
              {splitting && <Loader2 className="w-3.5 h-3.5 animate-spin absolute left-2 top-1/2 -translate-y-1/2 text-[#2D5F4F]" data-testid={`gs-jo-splitting-${s}`} />}
            </div>
          </div>
        </TableCell>
      </TableRow>
      {open && avail.own.map((c) => (
        <ColourRow key={c.color_name} id={`${s}-${slug(c.color_name)}`} c={c} value={row?.own?.[c.color_name]} disabled={disabled} onChange={(v) => onColour('own', c, v)} />
      ))}
      {open && a.parent && avail.parent.length > 0 && <ParentHead a={a} s={s} />}
      {open && avail.parent.map((c) => (
        <ColourRow key={`p-${c.color_name}`} id={`${s}-parent-${slug(c.color_name)}`} c={c} value={row?.parent?.[c.color_name]} disabled={disabled}
          tone="bg-indigo-50/30" onChange={(v) => onColour('parent', c, v)} />
      ))}
    </>
  );
};
